/**
 * ConfirmDialog — Confirmación destructiva con énfasis visual.
 * Reemplaza window.confirm() en operaciones que tienen consecuencias (eliminar, etc).
 *
 * Props:
 *   open, onClose, onConfirm,
 *   title, message, confirmText='Eliminar', cancelText='Cancelar',
 *   severity='danger'|'warning'|'info' (default 'danger'),
 *   loading?
 */
import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const STYLES = {
  danger:  { color: '#dc2626', bg: '#fee2e2', icon: ErrorOutlineIcon, btnColor: 'error' },
  warning: { color: '#d97706', bg: '#fef3c7', icon: WarningAmberIcon, btnColor: 'warning' },
  info:    { color: '#0891b2', bg: '#cffafe', icon: InfoOutlinedIcon, btnColor: 'primary' },
};

export default function ConfirmDialog({
  open, onClose, onConfirm,
  title, message,
  confirmText = 'Eliminar', cancelText = 'Cancelar',
  severity = 'danger', loading = false,
}) {
  const s = STYLES[severity] || STYLES.danger;
  const Icon = s.icon;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: 3, pb: 1 }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: '50%',
          bgcolor: s.bg, color: s.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon sx={{ fontSize: 24 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pb: 2 }}>
        <Typography sx={{ fontSize: 13.5, color: '#475569', lineHeight: 1.55 }}>
          {message}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} disabled={loading}
          sx={{ textTransform: 'none', color: '#64748b' }}>
          {cancelText}
        </Button>
        <Button onClick={onConfirm} variant="contained" color={s.btnColor} disableElevation
          disabled={loading}
          sx={{ textTransform: 'none', fontWeight: 600 }}>
          {loading ? 'Procesando...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
